import { inject, Injectable } from '@angular/core';
import { EnvironmentService, PinnaklEnvironmentAmplitudeConfiguration } from '@pinnakl/core/environment';
import {
  AmplitudeCRMEventTypes,
  AmplitudeErrorEventProperties,
  AmplitudeEventTypes,
  AmplitudeFeatureUsedEventProperties,
  AmplitudeHttpErrorEventProperties,
  AmplitudePOEMSEventTypes,
} from '@pinnakl/shared/types';

@Injectable({
  providedIn: 'root',
})
export class AmplitudeEventsCollector {
  private readonly environmentService = inject(EnvironmentService);
  private isInitialized = false;
  private amplitude?: typeof import('@amplitude/analytics-browser');

  async initialize(userEmail?: string, clientId?: number, debug = true): Promise<void> {
    const configuration =
      this.environmentService.get<PinnaklEnvironmentAmplitudeConfiguration>('amplitudeConfiguration');
    if (!configuration) {
      throw new Error('No Amplitude configuration!');
    }
    const [amplitude, { LogLevel }] = await Promise.all([
      import('@amplitude/analytics-browser'),
      import('@amplitude/analytics-core'),
    ]);
    this.amplitude = amplitude;
    amplitude
      .init(configuration.apiKey, userEmail, {
        logLevel: debug ? LogLevel.Debug : LogLevel.Warn,
        flushIntervalMillis: 5000,
        autocapture: {
          attribution: true,
          pageViews: true,
          sessions: true,
          formInteractions: true,
          fileDownloads: true,
          elementInteractions: true,
        },
      })
      .promise.then(() => (this.isInitialized = true));

    if (clientId) {
      const identifyEvent = new amplitude.Identify();
      identifyEvent.set('clientId', clientId);
      amplitude.identify(identifyEvent);
    }

    window.addEventListener('pagehide', () => {
      amplitude.setTransport('beacon');
      amplitude.flush();
    });
  }

  track(
    eventType: AmplitudeCRMEventTypes.GlobalError | AmplitudePOEMSEventTypes.GlobalError,
    eventProperties: AmplitudeErrorEventProperties,
  ): void;
  track(
    eventType: AmplitudeCRMEventTypes.ClientError | AmplitudePOEMSEventTypes.ClientError,
    eventProperties: AmplitudeErrorEventProperties,
  ): void;
  track(
    eventType: AmplitudeCRMEventTypes.HttpError | AmplitudePOEMSEventTypes.HttpError,
    eventProperties: AmplitudeHttpErrorEventProperties,
  ): void;
  track(
    eventType: AmplitudeCRMEventTypes.FeatureUsed | AmplitudePOEMSEventTypes.FeatureUsed,
    eventProperties: AmplitudeFeatureUsedEventProperties,
  ): void;
  track(eventType: AmplitudePOEMSEventTypes.TemporaryEventLogout, eventProperties: any): void;
  track(
    eventType: AmplitudeEventTypes,
    eventProperties:
      AmplitudeErrorEventProperties | AmplitudeHttpErrorEventProperties | AmplitudeFeatureUsedEventProperties,
  ): void {
    if (this.isInitialized) {
      this.amplitude?.track({
        event_type: eventType,
        event_properties: eventProperties,
      });
    }
  }
}
